/**
 * Forgot Password Screen
 * Allows users to request a password reset email
 * Includes "Email Sent" success state matching auth-screens-preview
 *
 * AUTH-012: the success state carries a 6-digit code input — the emailed
 * reset code is the primary path; the emailed deep link stays as fallback.
 */

import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import {
  Box,
  VStack,
  HStack,
  Text,
} from '@gluestack-ui/themed';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { colors, FS_XS, FS_SM, FS_MD } from '../../theme';
import { useAuthStore } from '../../store/authStore';

import {
  OTP_CODE_LENGTH,
  isValidOtpCode,
  sanitizeOtpCodeInput,
} from '../../auth/otpCode';
import { AUTH_RESEND_COOLDOWN_MS } from '../../config/timeouts';
import { useResendCooldown } from '../../hooks/useResendCooldown';
import { PillInput } from '../../components/auth/PillInput';
import { PillButton } from '../../components/auth/PillButton';
import { TextLinkButton } from '../../components/ui';
import { useAppBannerStore } from '../../hooks/useAppBanner';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FS_TITLE = 24;
const FS_HEADING = 28;
const FS_CHECKMARK = 48;

export interface ForgotPasswordScreenDisplayProps {
  email: string;
  touched: boolean;
  isValidEmail: boolean;
  isLoading: boolean;
  sent: boolean;
  error: string | null;
  code: string;
  codeTouched: boolean;
  isCodeValid: boolean;
  isVerifying: boolean;
  verifyError: string | null;
  resendSecondsLeft: number;
  isResending: boolean;
  onEmailChange: (text: string) => void;
  onBlur: () => void;
  onSubmit: () => void;
  onBackToLogin: () => void;
  onTryAgain: () => void;
  onCodeChange: (text: string) => void;
  onVerifyCode: () => void;
  onResendCode: () => void;
}

export function ForgotPasswordScreenDisplay({
  email,
  touched,
  isValidEmail,
  isLoading,
  sent,
  error,
  code,
  codeTouched,
  isCodeValid,
  isVerifying,
  verifyError,
  resendSecondsLeft,
  isResending,
  onEmailChange,
  onBlur,
  onSubmit,
  onBackToLogin,
  onTryAgain,
  onCodeChange,
  onVerifyCode,
  onResendCode,
}: ForgotPasswordScreenDisplayProps) {
  const { t } = useTranslation();
  const { theme, fontScale, ffs } = useDisplayTheme();

  const scaledCircle = Math.round(80 * fontScale);

  // Email Sent success state — code entry (primary) + emailed link (fallback)
  if (sent) {
    const resendDisabled = resendSecondsLeft > 0 || isResending;

    return (
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: theme.background }}
      >
        <ScrollView
          contentContainerStyle={{ flexGrow: 1 }}
          keyboardShouldPersistTaps="handled"
        >
          <Box
            flex={1}
            justifyContent="center"
            alignItems="center"
            px="$6"
            py="$8"
            style={{ backgroundColor: theme.background }}
          >
            <VStack space="md" alignItems="center" w="$full" maxWidth={400}>
              {/* Checkmark circle */}
              <View
                style={{
                  width: scaledCircle,
                  height: scaledCircle,
                  borderRadius: scaledCircle / 2,
                  backgroundColor: colors.success,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: 24,
                  shadowColor: colors.success,
                  shadowOffset: { width: 0, height: 8 },
                  shadowOpacity: 0.3,
                  shadowRadius: 16,
                  elevation: 8,
                }}
              >
                <Text allowFontScaling={false} style={{ color: 'white', fontSize: FS_CHECKMARK * fontScale, ...ffs }}>{'\u2713'}</Text>
              </View>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_TITLE * fontScale,
                  fontWeight: '700',
                  color: colors.primary,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {t('auth.forgotPassword.successTitle')}
              </Text>

              <Text
                allowFontScaling={false}
                style={{ fontSize: FS_MD * fontScale, color: theme.textSecondary, textAlign: 'center', lineHeight: 24 * fontScale, ...ffs }}
              >
                {t('auth.forgotPassword.successSentTo')}
              </Text>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_MD * fontScale,
                  color: theme.text,
                  fontWeight: '600',
                  textAlign: 'center',
                  marginTop: 8,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {email}
              </Text>

              {/* Code entry (AUTH-012) */}
              <Box w="$full">
                <PillInput
                  label={t('auth.forgotPassword.codeLabel')}
                  icon={'\u{1F522}'}
                  testID="forgot-input-code"
                  errorTestID="forgot-text-code-invalid"
                  accessibilityLabel={t('auth.forgotPassword.codeAccessibility')}
                  placeholder="123456"
                  value={code}
                  onChangeText={onCodeChange}
                  keyboardType="number-pad"
                  textContentType="oneTimeCode"
                  autoComplete="one-time-code"
                  maxLength={OTP_CODE_LENGTH}
                  editable={!isVerifying}
                  isInvalid={codeTouched && !isCodeValid}
                  errorText={t('auth.forgotPassword.codeError')}
                />

                <PillButton
                  testID="forgot-button-verify"
                  title={t('auth.forgotPassword.verifyCodeButton')}
                  onPress={onVerifyCode}
                  isLoading={isVerifying}
                  isDisabled={isVerifying || (codeTouched && !isCodeValid)}
                />

                {/* Verify error (invalid/expired code, rate limit, network) */}
                {verifyError && (
                  <Text
                    testID="forgot-text-code-error"
                    accessibilityLiveRegion="polite"
                    allowFontScaling={false}
                    style={{ fontSize: FS_SM * fontScale, color: colors.error, textAlign: 'center', marginTop: 8, ...ffs }}
                  >
                    {verifyError}
                  </Text>
                )}

                <HStack justifyContent="center" mt="$3">
                  <TextLinkButton
                    testID="forgot-link-resend"
                    accessibilityLabel={t('auth.forgotPassword.resendAccessibility')}
                    label={
                      resendSecondsLeft > 0
                        ? t('auth.forgotPassword.resendCountdown', { seconds: resendSecondsLeft })
                        : t('auth.forgotPassword.resendButton')
                    }
                    onPress={onResendCode}
                    disabled={resendDisabled}
                  />
                </HStack>
              </Box>

              <Text
                allowFontScaling={false}
                style={{
                  fontSize: FS_SM * fontScale,
                  color: theme.textSecondary,
                  textAlign: 'center',
                  lineHeight: 22 * fontScale,
                  marginBottom: 8,
                  ...ffs
                }}
              >
                {t('auth.forgotPassword.successExpiry')}
              </Text>

              <HStack space="sm" alignItems="center" mb="$6">
                <Text allowFontScaling={false} style={{ fontSize: 16 * fontScale, ...ffs }}>{'\u{1F4A1}'}</Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: colors.warning, ...ffs }}>
                  {t('auth.forgotPassword.successTip')}
                </Text>
              </HStack>

              <PillButton
                testID="forgot-button-back2"
                title={t('auth.forgotPassword.successBackButton')}
                onPress={onBackToLogin}
              />

              <HStack justifyContent="center" space="xs">
                <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                  {t('auth.forgotPassword.successRetryText')}
                </Text>
                <TextLinkButton testID="forgot-link-retry" accessibilityLabel={t('auth.forgotPassword.successRetryAccessibility')} label={t('auth.forgotPassword.successRetryLink')} onPress={onTryAgain} />
              </HStack>
            </VStack>
          </Box>
        </ScrollView>
      </KeyboardAvoidingView>
    );
  }

  // Forgot Password form state
  return (
    <KeyboardAvoidingView
      style={{ flex: 1, backgroundColor: theme.background }}
    >
      <ScrollView
        contentContainerStyle={{ flexGrow: 1 }}
        keyboardShouldPersistTaps="handled"
      >
        <Box flex={1} justifyContent="center" alignItems="center" px="$6">
          {/* Heading */}
          <VStack space="sm" mb="$8" alignItems="center">
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_HEADING * fontScale,
                fontWeight: '700',
                color: theme.text,
                ...ffs
              }}
            >
              {t('auth.forgotPassword.title')}
            </Text>
            <Text
              allowFontScaling={false}
              style={{
                fontSize: FS_MD * fontScale,
                color: theme.textSecondary,
                textAlign: 'center',
                lineHeight: 22 * fontScale,
                ...ffs
              }}
            >
              {t('auth.forgotPassword.subtitle')}
            </Text>
          </VStack>

          {/* Form */}
          <Box w="$full" maxWidth={400}>
            <PillInput
              label={t('auth.forgotPassword.emailLabel')}
              icon={'\u2709\uFE0F'}
              testID="forgot-input-email"
              accessibilityLabel={t('auth.forgotPassword.emailAccessibility')}
              placeholder="you@example.com"
              value={email}
              onChangeText={onEmailChange}
              onBlur={onBlur}
              autoCapitalize="none"
              keyboardType="email-address"
              editable={!isLoading}
              isInvalid={touched && !isValidEmail}
              errorText={t('auth.forgotPassword.emailError')}
            />

            <PillButton
              testID="forgot-button-submit"
              title={t('auth.forgotPassword.sendResetLink')}
              onPress={onSubmit}
              isLoading={isLoading}
              isDisabled={isLoading || (touched && !isValidEmail)}
            />

            {/* Error Display */}
            {error && (
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: colors.error, textAlign: 'center', marginTop: 8, ...ffs }}>
                {error}
              </Text>
            )}

            {/* Back to Login Link */}
            <HStack justifyContent="center" mt="$6" space="xs">
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('auth.forgotPassword.rememberPassword')}
              </Text>
              <TextLinkButton testID="forgot-link-login" accessibilityLabel={t('auth.forgotPassword.loginAccessibility')} accessibilityRole="link" label={t('auth.forgotPassword.loginLink')} onPress={onBackToLogin} disabled={isLoading} />
            </HStack>
          </Box>
        </Box>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

export function ForgotPasswordScreen() {
  const navigation = useNavigation();
  const { resetPassword, verifyRecoveryCode, isLoading, error, clearError } = useAuthStore();

  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [sent, setSent] = useState(false);

  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const resendCooldown = useResendCooldown(AUTH_RESEND_COOLDOWN_MS);

  const isValidEmail = EMAIL_REGEX.test(email);
  const isCodeValid = isValidOtpCode(code);

  const resetCodeEntry = () => {
    setCode('');
    setCodeTouched(false);
    setVerifyError(null);
  };

  const handleResetPassword = async () => {
    setTouched(true);

    if (!isValidEmail) {
      return;
    }

    try {
      await resetPassword(email);
      resetCodeEntry();
      setSent(true);
      // The send itself opens Supabase's max_frequency window
      resendCooldown.start();
    } catch (err: unknown) {
      useAppBannerStore.getState().showBanner({ message: (err as Error).message });
    }
  };

  const handleCodeChange = (text: string) => {
    setCode(sanitizeOtpCodeInput(text));
    if (verifyError) setVerifyError(null);
  };

  const handleVerifyCode = async () => {
    setCodeTouched(true);

    // Client-side guard: no provider call for a malformed code
    if (!isValidOtpCode(code)) {
      return;
    }

    setIsVerifying(true);
    setVerifyError(null);
    try {
      // Establishes the provider-level recovery session (like the deep link);
      // ResetPasswordScreen's updatePassword completes the flow.
      await verifyRecoveryCode(email, code);
      navigation.navigate('ResetPassword' as never);
    } catch (err: unknown) {
      setVerifyError((err as Error).message);
      // Clear for retry — a consumed/expired code can't succeed on re-submit
      setCode('');
      setCodeTouched(false);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown.isCoolingDown || isResending) return;
    setIsResending(true);
    try {
      await resetPassword(email);
      setVerifyError(null);
      resendCooldown.start();
    } catch (err: unknown) {
      useAppBannerStore.getState().showBanner({ message: (err as Error).message });
    } finally {
      setIsResending(false);
    }
  };

  // Clear error when email changes
  // Intentional: only react to email changes, not error/sent state
  /* eslint-disable react-hooks/exhaustive-deps */
  React.useEffect(() => {
    if (error) clearError();
    if (sent) {
      setSent(false);
      resetCodeEntry();
      resendCooldown.reset();
    }
  }, [email]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return (
    <ForgotPasswordScreenDisplay
      email={email}
      touched={touched}
      isValidEmail={isValidEmail}
      isLoading={isLoading}
      sent={sent}
      error={error}
      code={code}
      codeTouched={codeTouched}
      isCodeValid={isCodeValid}
      isVerifying={isVerifying}
      verifyError={verifyError}
      resendSecondsLeft={resendCooldown.secondsLeft}
      isResending={isResending}
      onEmailChange={setEmail}
      onBlur={() => setTouched(true)}
      onSubmit={handleResetPassword}
      onBackToLogin={() => navigation.goBack()}
      onTryAgain={() => {
        setSent(false);
        resetCodeEntry();
      }}
      onCodeChange={handleCodeChange}
      onVerifyCode={handleVerifyCode}
      onResendCode={handleResendCode}
    />
  );
}
